/* Reports (docs/plans/phase1-reports.md §9). Tokens only; flat surfaces, tables at 0 radius,
   everything readable at 1366x768 without horizontal scrolling. */

.page {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  padding: var(--space-5);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  background: var(--paper);
  box-shadow: var(--shadow-card);
}

/* The day summary per facility: the sentence, then each block a clear step below. */
.summarySection {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  min-width: 0;
  padding: var(--space-5);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  background: var(--paper);
  box-shadow: var(--shadow-card);
}

/* Each report section is a white card on the canvas (ADR 0017), as packages/ui Section. */
@media (width < 640px) {
  .section,
  .summarySection {
    padding: var(--space-4);
  }
}

.sectionHeading {
  font: var(--t-heading);
}

.groupHeading {
  font: var(--t-heading);
}

.muted {
  color: var(--graphite);
  font: var(--t-small);
}

.summary {
  max-width: 75ch;
  font: var(--t-body);
}

/* Fields line up by their inputs even when one has helper text below it. */
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--space-3) var(--space-4);
}

.filters > * {
  flex: 0 1 14rem;
  min-width: 0;
}

/* "Show report" sits on the input line: a label's height (22px) plus the field's gap. */
.filters > :last-child {
  flex: 0 0 auto;
  padding-top: calc(22px + var(--space-1));
}

/* Day summary: a heading and what belongs to it, then the next one a clear step below. */
.block {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.subHeading {
  font: var(--t-body-strong);
}

/* Two compact tables side by side where there is room (DESIGN.md §8, owner's summary). */
.pair {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 22rem), 1fr));
  gap: var(--space-5) var(--space-6);
  align-items: start;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

/* The index: plain lists with rule lines, a link and one line of description. */
.list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--rule);
  list-style: none;
}

.listRow {
  display: grid;
  grid-template-columns: minmax(14rem, 20rem) minmax(0, 1fr);
  gap: var(--space-1) var(--space-4);
  align-items: baseline;
  min-height: var(--row-height);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--rule);
}

@media (width < 48rem) {
  .listRow {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* Screen tables: header in t-micro, rule lines, numbers right-aligned on tabular figures. */
.table {
  width: 100%;
  border-collapse: collapse;
  font: var(--t-body);
  font-variant-numeric: tabular-nums;
}

.table th {
  padding: var(--space-2) var(--space-2);
  border-bottom: 1px solid var(--rule);
  color: var(--graphite);
  font: var(--t-micro);
  font-weight: 600;
  text-align: left;
  vertical-align: bottom;
}

.table td {
  padding: var(--space-2) var(--space-2);
  border-bottom: 1px solid var(--rule);
  vertical-align: top;
  overflow-wrap: anywhere;
}

.table [data-numeric] {
  text-align: right;
  white-space: nowrap;
}

.table tfoot td {
  font: var(--t-body-strong);
  font-variant-numeric: tabular-nums;
}

.secondLine {
  display: block;
  color: var(--graphite);
  font: var(--t-small);
}

.flag {
  font: var(--t-body-strong);
}

/* Critical lab results: the one clinical-danger use on these screens (DESIGN.md §7). */
.critical {
  color: var(--danger);
  font: var(--t-body-strong);
}

/* The A4 sheet is printed, not shown: it sits off screen until printSheet marks it. */
.printOnly {
  position: absolute;
  top: 0;
  left: -10000px;
}

@media print {
  .printOnly {
    left: 0;
  }
}
